<template>
  <div class="home">
    <!-- HERO -->
    <header class="home-hero">
      <galeryPhoto />
      <div class="home-hero-shade"></div>

      <div class="home-hero-content">
        <p class="home-hero-eyebrow">Parroquia Castrense</p>
        <h1 class="home-hero-title">Santo Domingo</h1>
        <p class="home-hero-city">{{ $root.constantes.nombre_ciudad }}</p>

        <div class="home-hero-actions">
          <a v-track:clic_portada="{ boton: 'horarios' }" href="#horarios" class="home-btn home-btn--primary" @click.prevent="scrollTo('horarios')">
            <span class="material-symbols-outlined">schedule</span>
            Horarios de misa
          </a>
          <router-link v-track:clic_portada="{ boton: 'calendario' }" :to="{ name: 'Eventos' }" class="home-btn home-btn--ghost">
            <span class="material-symbols-outlined">calendar_month</span>
            Calendario de eventos
          </router-link>
        </div>
      </div>

      <button type="button" class="home-scroll" aria-label="Ir al contenido" @click="scrollTo('contenido')">
        <span class="material-symbols-outlined">keyboard_arrow_down</span>
      </button>
    </header>

    <!-- CONTENIDO -->
    <main id="contenido" class="home-body">
      <div class="home-container">

        <!-- HORARIOS + EVENTOS -->
        <horarios-item />

        <!-- ÚLTIMAS NOTICIAS -->
        <section class="home-section" aria-labelledby="titulo-noticias">
          <div class="home-section-head">
            <div>
              <p class="home-section-eyebrow">Actualidad</p>
              <h2 id="titulo-noticias" class="home-section-title">Últimas noticias</h2>
            </div>
            <router-link :to="{ name: 'vistaNoticias' }" class="home-link">
              Ver todas <span class="material-symbols-outlined">arrow_forward</span>
            </router-link>
          </div>

          <div v-if="loadingNews" class="home-grid">
            <div v-for="n in 3" :key="n" class="home-skeleton"></div>
          </div>
          <p v-else-if="!news.length" class="home-empty">Todavía no hay noticias publicadas.</p>
          <div v-else class="home-grid">
            <ultimas-noticias
              v-for="item in news.slice(0, 6)"
              :key="(item.paginaPrincipal ? 'c' : 'n') + item.id"
              :imageSrc="item.imagenPortada"
              :newTitle="item.titulo"
              :resumenNoticia="item.resumenNoticia"
              :fechaPublicacion="item.fechaPublicacion"
              :idnoticia="item.paginaPrincipal ? 'caritas-' + item.id : item.id"
              :caritas="!!item.paginaPrincipal"
              @getIdNews="getId"
            />
          </div>
        </section>

        <!-- AVISOS AL MÓVIL + CANAL DE WHATSAPP -->
        <mantente-informado origen="portada" />

        <!-- SITIOS DE INTERÉS -->
        <section v-if="loadingLinks || enlacesInteres.length" class="home-section" aria-labelledby="titulo-sitios">
          <div class="home-section-head">
            <div>
              <p class="home-section-eyebrow">Enlaces</p>
              <h2 id="titulo-sitios" class="home-section-title">Sitios de interés</h2>
            </div>
          </div>

          <div v-if="loadingLinks" class="home-grid home-grid--links">
            <div v-for="n in 3" :key="n" class="home-skeleton home-skeleton--short"></div>
          </div>
          <div v-else class="home-grid home-grid--links">
            <sitios-interes v-for="(item, index) in enlacesInteres" :key="item.id || index" :data="item" />
          </div>
        </section>

        <!-- UBICACIÓN -->
        <section class="home-section" aria-labelledby="titulo-ubicacion">
          <div class="home-section-head">
            <div>
              <p class="home-section-eyebrow">Visítanos</p>
              <h2 id="titulo-ubicacion" class="home-section-title">Dónde estamos</h2>
            </div>
          </div>

          <div class="home-location">
            <div class="home-location-info">
              <h3>Iglesia Castrense de Santo Domingo</h3>
              <ul>
                <li>
                  <span class="material-symbols-outlined">location_on</span>
                  <span class="home-location-text">{{ direccion }}</span>
                </li>
                <li v-if="$root.constantes.tlf.iglesia">
                  <span class="material-symbols-outlined">call</span>
                  <a v-track:contacto="{ metodo: 'telefono', origen: 'portada' }" :href="`tel:${$root.constantes.tlf.iglesia}`">{{ telefonoFormateado }}</a>
                </li>
                <li v-if="$root.constantes.email.iglesia">
                  <span class="material-symbols-outlined">mail</span>
                  <a v-track:contacto="{ metodo: 'email', origen: 'portada' }" :href="`mailto:${$root.constantes.email.iglesia}`">{{ $root.constantes.email.iglesia }}</a>
                </li>
              </ul>
              <a v-track:contacto="{ metodo: 'como_llegar', origen: 'portada' }" :href="comoLlegarUrl" target="_blank" rel="noopener" class="home-btn home-btn--primary">
                <span class="material-symbols-outlined">directions</span>
                Cómo llegar
              </a>
            </div>

            <div class="home-location-map">
              <iframe
                title="Mapa de la Iglesia Castrense de Santo Domingo"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1117.6022287337419!2d-0.9867959038681466!3d37.60083024691427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd634228cc2985b5%3A0x96b140c99ff6040c!2sIglesia%20Castrense%20de%20Santo%20Domingo!5e0!3m2!1ses!2ses!4v1733524140206!5m2!1ses!2ses"
                allowfullscreen
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import galeryPhoto from "@/components/GaleryPhoto.vue";
import ultimasNoticias from "@/views/website/paginaPrincipal/UltimasNoticias.vue";
import axios from "@/directives/axios.js";
import HorariosItem from "@/components/website/home/HorariosItem.vue";
import sitiosInteres from "@/components/SitiosInteres.vue";
import MantenteInformado from "@/components/avisos/MantenteInformado.vue";

export default {
  name: "HomePage",
  components: {
    HorariosItem,
    galeryPhoto,
    ultimasNoticias,
    sitiosInteres,
    MantenteInformado,
  },
  metaInfo() {
    return {
      title: this.$root.constantes.nombre_sitio,
      meta: [
        {
          name: "description",
          content: `Informacion sobre la ${this.$root.constantes.nombre_sitio}.`,
        },
        {
          name: "keywords",
          content: `${this.$root.constantes.nombre_sitio}, ${this.$root.constantes.nombre_sitio}, misas ${this.$root.constantes.nombre_ciudad}, misas de la armada ${this.$root.constantes.nombre_ciudad}`,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: "https://santodomingocartagena.com",
        },
      ],
    };
  },
  data() {
    return {
      news: [],
      enlacesInteres: [],
      loadingNews: true,
      loadingLinks: true,
    };
  },
  computed: {
    direccion() {
      return (this.$root.constantes.direccion || "").trim();
    },
    telefonoFormateado() {
      // +34968504759 -> +34 968 50 47 59
      const tlf = this.$root.constantes.tlf.iglesia || "";
      const m = tlf.match(/^(\+34)(\d{3})(\d{2})(\d{2})(\d{2})$/);
      return m ? `${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]}` : tlf;
    },
    comoLlegarUrl() {
      return (
        "https://www.google.com/maps/dir/?api=1&destination=" +
        encodeURIComponent("Iglesia Castrense de Santo Domingo, " + this.direccion)
      );
    },
  },
  methods: {
    async obtenerNoticias() {
      try {
        const { data } = await axios.get("/noticias/ultimasNoticias");
        const todas = [...(data.noticias || []), ...(data.noticiasCaritas || [])];
        this.news = todas.sort((a, b) =>
          a.fechaPublicacion > b.fechaPublicacion ? -1 : a.fechaPublicacion < b.fechaPublicacion ? 1 : 0
        );
      } catch (e) {
        this.news = [];
      } finally {
        this.loadingNews = false;
      }
    },
    async obtenerEnlacesInteres() {
      try {
        const { data } = await axios.get("/enlaces/todosEnlacesActivos");
        // La API ya los devuelve por posición (ascendente); se reordena por si acaso
        this.enlacesInteres = [...(data.todosEnlaces || [])].sort(
          (a, b) => (Number(a.posicion) || 0) - (Number(b.posicion) || 0)
        );
      } catch (e) {
        this.enlacesInteres = [];
      } finally {
        this.loadingLinks = false;
      }
    },
    getId(value) {
      this.$router.push({
        name: "noticia",
        query: { id: value.id },
      });
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (!el) return;
      const nav = document.querySelector(".cabecera");
      const offset = nav ? nav.offsetHeight : 0;
      const top = el.getBoundingClientRect().top + window.scrollY - offset - 16;
      window.scrollTo({ top, behavior: "smooth" });
    },
  },
  mounted() {
    this.$emit("datosCargados", false);
    this.obtenerNoticias();
    this.obtenerEnlacesInteres();
  },
};
</script>

<style scoped>
.home {
  text-align: left;
  font-family: var(--sd-font-body);
  color: var(--sd-text);
}

/* ---------- Hero ---------- */
.home-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100svh;
  padding: 176px 20px 96px;
  text-align: center;
  isolation: isolate;
}

.home-hero-shade {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(180deg, rgba(11, 18, 33, 0.35) 0%, rgba(11, 18, 33, 0.15) 40%, rgba(11, 18, 33, 0.7) 100%);
}

.home-hero-content {
  max-width: 760px;
  animation: home-rise 0.9s ease-out both;
}

.home-hero-eyebrow {
  margin: 0 0 10px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #f3dfae;
}

.home-hero-title {
  margin: 0;
  font-family: var(--sd-font-display);
  font-size: clamp(2.6rem, 8vw, 5.5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.45);
}

.home-hero-city {
  margin: 8px 0 0;
  font-family: var(--sd-font-display);
  font-size: clamp(1.1rem, 2.6vw, 1.6rem);
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
}

.home-hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 36px;
}

.home-scroll {
  position: absolute;
  bottom: 24px;
  left: 50%;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
  transform: translateX(-50%);
  cursor: pointer;
  animation: home-bounce 2.2s ease-in-out infinite;
}

.home-scroll .material-symbols-outlined {
  font-size: 28px;
  color: #fff;
}

@keyframes home-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes home-bounce {
  0%,
  100% {
    transform: translate(-50%, 0);
  }
  50% {
    transform: translate(-50%, 8px);
  }
}

/* ---------- Botones ---------- */
.home-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.15s, background 0.15s, box-shadow 0.15s;
}

.home-btn:hover {
  transform: translateY(-1px);
  text-decoration: none;
}

.home-btn .material-symbols-outlined {
  font-size: 20px;
  color: inherit;
}

.home-btn--primary {
  background: var(--sd-primary);
  color: var(--sd-primary-contrast);
  box-shadow: 0 6px 20px rgba(29, 79, 134, 0.35);
}

.home-btn--primary:hover {
  background: var(--sd-primary-strong);
  color: var(--sd-primary-contrast);
}

.home-btn--ghost {
  border: 1px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  backdrop-filter: blur(6px);
}

.home-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

/* ---------- Cuerpo ---------- */
.home-body {
  position: relative;
  background: var(--sd-bg);
  border-radius: 28px 28px 0 0;
  box-shadow: 0 -20px 40px rgba(0, 0, 0, 0.15);
}

.home-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 64px 20px 80px;
  display: flex;
  flex-direction: column;
  gap: 88px;
}

.home-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
}

.home-section-eyebrow {
  margin: 0 0 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.home-section-title {
  margin: 0;
  font-family: var(--sd-font-display);
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  color: var(--sd-text);
}

.home-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 0.92rem;
  color: var(--sd-primary);
  text-decoration: none;
  white-space: nowrap;
}

.home-link .material-symbols-outlined {
  font-size: 18px;
  color: inherit;
  transition: transform 0.15s;
}

.home-link:hover {
  color: var(--sd-primary-strong);
  text-decoration: none;
}

.home-link:hover .material-symbols-outlined {
  transform: translateX(3px);
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
}

.home-grid--links {
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.home-empty {
  margin: 0;
  padding: 40px;
  border: 1px dashed var(--sd-border);
  border-radius: var(--sd-radius);
  text-align: center;
  color: var(--sd-muted);
}

.home-skeleton {
  height: 380px;
  border-radius: var(--sd-radius);
  background: linear-gradient(90deg, var(--sd-surface-2) 25%, var(--sd-surface) 50%, var(--sd-surface-2) 75%);
  background-size: 200% 100%;
  animation: home-shimmer 1.4s infinite linear;
}

.home-skeleton--short {
  height: 110px;
}

@keyframes home-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

/* ---------- Ubicación ---------- */
.home-location {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.6fr);
  overflow: hidden;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
}

.home-location-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding: 32px;
}

.home-location-info h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--sd-text);
}

.home-location-info ul {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.home-location-info li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.home-location-info li .material-symbols-outlined {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 20px;
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.home-location-info li .home-location-text,
.home-location-info li a {
  padding-top: 7px;
  color: var(--sd-text);
  text-decoration: none;
  word-break: break-word;
}

.home-location-info li a:hover {
  color: var(--sd-primary);
  text-decoration: underline;
}

.home-location-map {
  min-height: 340px;
}

.home-location-map iframe {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 340px;
  border: 0;
}

.dark .home-location-map iframe {
  filter: invert(0.9) hue-rotate(180deg) saturate(0.7);
}

/* ---------- Responsive ---------- */
@media (max-width: 860px) {
  .home-location {
    grid-template-columns: 1fr;
  }

  .home-location-map,
  .home-location-map iframe {
    min-height: 280px;
  }
}

@media (max-width: 640px) {
  .home-hero {
    padding-top: 130px;
  }

  .home-hero-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .home-btn {
    justify-content: center;
  }

  .home-container {
    padding: 44px 16px 64px;
    gap: 64px;
  }

  .home-grid {
    grid-template-columns: 1fr;
  }

  .home-location-info {
    padding: 24px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-hero-content,
  .home-scroll,
  .home-skeleton {
    animation: none;
  }
}
</style>
